import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Divider,
  Box,
  IconButton
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import SettingsIcon from '@mui/icons-material/Settings';
import LinkIcon from '@mui/icons-material/Link';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import Palette from '../../../ui-component/ThemePalette';
import { patchApi } from 'views/services/api';
import { hotelSchema } from 'schema';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import PropTypes from 'prop-types';
import { parse } from 'date-fns';
import { getTimezones } from 'utils/timeZone';
import { getCountries } from 'utils/countries';

const EditHotel = (props) => {
  const { open, handleClose, data } = props;
  const [previewUrl, setPreviewUrl] = useState(null);

  const initialValues = {
    email: data?.email || '',
    name: data?.name || '',
    managerName: data?.managerName || '',
    contact: data?.contact || '',
    managerPhone: data?.managerPhone || '',
    country: data?.country || '',
    address: data?.address || '',
    gstNumber: data?.gstNumber || '',
    foodgstpercentage: data?.foodgstpercentage || '',
    roomgstpercentage: data?.roomgstpercentage || '',
    mapurl: data?.mapurl || '',
    websiteURL: data?.websiteURL || '',
    googleReviewURL: data?.googleReviewURL || '',
    currency: data?.currency || 'USD',
    timezone: data?.timezone || 'America/Caracas',
    checkInTime: data?.checkInTime ? parse(data.checkInTime, 'hh:mm aa', new Date()) : null,
    checkOutTime: data?.checkOutTime ? parse(data.checkOutTime, 'hh:mm aa', new Date()) : null,
    cleaningBufferMinutes: data?.cleaningBufferMinutes ?? 30,
    hotelImage: null
  };

  useEffect(() => {
    if (data?.hotelImage) {
      setPreviewUrl(data.hotelImage);
    }
  }, [data]);

  const updateAdmin = async (values) => {
    try {
      const formattedValues = { ...values };
      
      if (values.checkInTime) {
        formattedValues.checkInTime = values.checkInTime.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      }
      if (values.checkOutTime) {
        formattedValues.checkOutTime = values.checkOutTime.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      }

      const formData = new FormData();
      Object.keys(formattedValues).forEach((key) => {
        if (key === 'hotelImage') {
          if (formattedValues.hotelImage) {
            formData.append('hotelImage', formattedValues.hotelImage);
          }
        } else if (formattedValues[key] !== undefined && formattedValues[key] !== null) {
          formData.append(key, formattedValues[key]);
        }
      });

      let response = await patchApi(`api/hotel/edit/${data?._id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 200) {
        toast.success('Perfil del hotel actualizado correctamente');
        handleClose();
        window.location.reload();
      } else {
        toast.error('No se pudo actualizar el hotel');
      }
    } catch (e) {
      console.error(e);
      toast.error('Error al actualizar el hotel');
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: hotelSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      updateAdmin(values);
    }
  });

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      formik.setFieldValue('hotelImage', file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const SectionTitle = ({ icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
      {icon}
      <Typography variant="h5" sx={{ ml: 1, fontWeight: 600, color: Palette.info }}>
        {title}
      </Typography>
    </Box>
  );

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        background: 'linear-gradient(45deg, #1e88e5 30%, #1565c0 90%)',
        color: 'white',
        py: 2
      }}>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
          Editar Perfil del Hotel
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 4 }}>
        <form onSubmit={formik.handleSubmit}>
          
          {/* Logo Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4, textAlign: 'center' }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1.5, fontWeight: 600 }}>
              Logo del Hotel
            </Typography>

            {/* Preview Area */}
            <Box
              sx={{
                width: 220,
                height: 80,
                mb: 1.5,
                bgcolor: '#f5f5f5',
                border: '2px dashed #ddd',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                p: 1
              }}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Logo actual del hotel"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <HomeWorkIcon sx={{ fontSize: 32, color: '#bbb' }} />
                  <Typography variant="caption" color="textSecondary">Sin logo</Typography>
                </Box>
              )}
            </Box>

            {/* Hidden file input */}
            <input
              accept="image/png, image/jpeg, image/jpg"
              style={{ display: 'none' }}
              id="hotel-logo-upload"
              type="file"
              onChange={handleImageChange}
            />

            {/* Visible Upload Button */}
            <label htmlFor="hotel-logo-upload" aria-label="Cambiar logo del hotel">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                size="small"
                sx={{ mt: 0.5 }}
              >
                {previewUrl ? 'Cambiar Logo' : 'Subir Logo'}
              </Button>
            </label>

            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
              Formato: PNG/JPG (Máx. 2MB) · Recomendado: 178x40px
            </Typography>
          </Box>


          <Divider />

          {/* General Information */}
          <SectionTitle icon={<HomeWorkIcon color="info" />} title="Información General" />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                id="name"
                name="name"
                label="Nombre del Hotel"
                fullWidth
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="email"
                name="email"
                label="Correo Electrónico"
                fullWidth
                disabled
                value={formik.values.email}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="managerName"
                name="managerName"
                label="Persona Encargada"
                fullWidth
                value={formik.values.managerName}
                onChange={formik.handleChange}
                helperText="Responsable del hotel (lead / contacto)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="country"
                name="country"
                label="País"
                select
                SelectProps={{ native: true }}
                fullWidth
                value={formik.values.country}
                onChange={formik.handleChange}
              >
                <option value="">Selecciona un país</option>
                {getCountries().map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="contact"
                name="contact"
                label="Teléfono del Hotel"
                fullWidth
                value={formik.values.contact}
                onChange={formik.handleChange}
                error={formik.touched.contact && Boolean(formik.errors.contact)}
                helperText={formik.touched.contact && formik.errors.contact}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="managerPhone"
                name="managerPhone"
                label="Teléfono del Encargado"
                fullWidth
                value={formik.values.managerPhone}
                onChange={formik.handleChange}
                placeholder="+58 414..."
                helperText="Número internacional completo. También autoriza el Asistente Sego IA."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="timezone"
                name="timezone"
                label="Zona Horaria"
                select
                SelectProps={{ native: true }}
                fullWidth
                value={formik.values.timezone}
                onChange={formik.handleChange}
              >
                {getTimezones().map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="currency"
                name="currency"
                label="Moneda Principal"
                select
                SelectProps={{ native: true }}
                fullWidth
                disabled
                value={formik.values.currency}
                onChange={formik.handleChange}
                error={formik.touched.currency && Boolean(formik.errors.currency)}
                helperText={formik.touched.currency && formik.errors.currency}
              >
                <option value="USD">USD - Dólar estadounidense</option>
                <option value="EUR">EUR - Euro</option>
                <option value="MXN">MXN - Peso mexicano</option>
                <option value="ARS">ARS - Peso argentino</option>
                <option value="BOB">BOB - Boliviano</option>
                <option value="BRL">BRL - Real brasileño</option>
                <option value="CLP">CLP - Peso chileno</option>
                <option value="COP">COP - Peso colombiano</option>
                <option value="CRC">CRC - Colón costarricense</option>
                <option value="CUP">CUP - Peso cubano</option>
                <option value="DOP">DOP - Peso dominicano</option>
                <option value="GTQ">GTQ - Quetzal guatemalteco</option>
                <option value="HNL">HNL - Lempira hondureño</option>
                <option value="NIO">NIO - Córdoba nicaragüense</option>
                <option value="PAB">PAB - Balboa panameño</option>
                <option value="PEN">PEN - Sol peruano</option>
                <option value="PYG">PYG - Guaraní paraguayo</option>
                <option value="UYU">UYU - Peso uruguayo</option>
                <option value="VES">VES - Bolívar venezolano</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="address"
                name="address"
                label="Dirección Física"
                fullWidth
                multiline
                rows={2}
                value={formik.values.address}
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
              />
            </Grid>
          </Grid>

          {/* Configuration */}
          <SectionTitle icon={<SettingsIcon color="info" />} title="Configuración de Estancía" />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label="Hora de Check-In"
                  value={formik.values.checkInTime}
                  onChange={(val) => formik.setFieldValue('checkInTime', val)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label="Hora de Check-Out"
                  value={formik.values.checkOutTime}
                  onChange={(val) => formik.setFieldValue('checkOutTime', val)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                id="cleaningBufferMinutes"
                name="cleaningBufferMinutes"
                label="Buffer de Limpieza (min)"
                type="number"
                fullWidth
                value={formik.values.cleaningBufferMinutes}
                onChange={formik.handleChange}
                helperText="Minutos entre reservas"
                inputProps={{ min: 0, max: 240 }}
              />
            </Grid>
          </Grid>

          {/* Taxes */}
          <SectionTitle icon={<ReceiptIcon color="info" />} title="Identificación e Impuestos" />
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                id="gstNumber"
                name="gstNumber"
                label="RIF / Identificación Fiscal"
                placeholder="Ej. J-12345678-9"
                fullWidth
                value={formik.values.gstNumber}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="foodgstpercentage"
                name="foodgstpercentage"
                label="IVA Alimentos (%)"
                type="number"
                fullWidth
                value={formik.values.foodgstpercentage}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="roomgstpercentage"
                name="roomgstpercentage"
                label="IVA Habitaciones (%)"
                type="number"
                fullWidth
                value={formik.values.roomgstpercentage}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>

          {/* Links */}
          <SectionTitle icon={<LinkIcon color="info" />} title="Enlaces y Presencia Online" />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                id="mapurl"
                name="mapurl"
                label="URL de Google Maps"
                placeholder="https://goo.gl/maps/..."
                fullWidth
                value={formik.values.mapurl}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="websiteURL"
                name="websiteURL"
                label="Sitio Web Oficial"
                placeholder="https://mihotel.com"
                fullWidth
                value={formik.values.websiteURL}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="googleReviewURL"
                name="googleReviewURL"
                label="URL de Reseñas de Google"
                fullWidth
                value={formik.values.googleReviewURL}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
          
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
        <Button onClick={handleClose} color="inherit" variant="text">
          Cancelar
        </Button>
        <Button 
          onClick={formik.handleSubmit} 
          variant="contained" 
          size="large"
          sx={{ 
            px: 4,
            backgroundColor: Palette.info,
            '&:hover': { backgroundColor: '#1565c0' }
          }}
        >
          Guardar Cambios
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditHotel.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  data: PropTypes.object
};

export default EditHotel;
